import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Layers, Cpu, Landmark } from 'lucide-react';

const Layer = ({ tag, icon: Icon, title, items, accent, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className={`relative p-6 md:p-8 rounded-lg border ${accent} bg-abyss-900/30 backdrop-blur-sm`}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 rounded-md bg-abyss-950 border border-teal-500/30 text-teal-400">
        <Icon size={16} />
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-teal-500/60">{tag}</div>
        <div className="text-lg font-serif text-sand-50">{title}</div>
      </div>
    </div>
    <p className="text-sm text-sand-50/60 leading-relaxed font-mono">{items}</p>
  </motion.div>
);

const StackRow = ({ name, role, note, last }) => (
  <div className="relative">
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-4">
      <span className="text-sm font-mono uppercase tracking-widest text-teal-400 whitespace-nowrap w-40 shrink-0">
        {name}
      </span>
      <span className="text-sm text-sand-50/70 leading-relaxed">
        <span className="text-sand-50">{role}</span> {note}
      </span>
    </div>
    {!last && (
      <div className="flex justify-start pl-14">
        <ArrowDown size={14} className="text-teal-500/40" />
      </div>
    )}
  </div>
);

const SystemsCompose = () => {
  return (
    <section
      data-testid="systems-compose"
      className="py-32 px-6 border-t border-abyss-900 bg-abyss-950/30 relative"
    >
      <div className="max-w-5xl mx-auto space-y-16">
        <div className="space-y-6 max-w-3xl">
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-teal-400/60 flex items-center gap-2">
            <Layers size={12} />
            How the systems compose
          </h2>
          <h3 className="text-3xl md:text-4xl font-serif text-sand-50 leading-tight">
            One thesis, three layers, six instruments.
          </h3>
          <p className="text-lg font-light text-sand-50/60 leading-relaxed">
            The six systems work as a stack. Field evidence rises into translation engines, which resolve into bounded,
            inspectable decisions. Read from the ground up.
          </p>
        </div>

        {/* Three-layer stack (top -> bottom visually, capital on top) */}
        <div className="space-y-4">
          <Layer
            index={0}
            tag="Layer 3 · Capital & Decision"
            icon={Landmark}
            title="What a committee can inspect"
            items="TNFD LEAP evidence packages · natural capital baselines · covenant and claim traceability · screening-grade site assessment · governed decision graphs"
            accent="border-teal-500/40"
          />
          <div className="flex justify-center">
            <ArrowDown size={18} className="text-teal-500/40 rotate-180" />
          </div>
          <Layer
            index={1}
            tag="Layer 2 · Translation Engines"
            icon={Cpu}
            title="Where structure becomes signal"
            items="AI Ecologist reconstructs food webs · Fluvion maps atmospheric moisture dependency · Upper Colorado scores parcels against real monitoring outcomes · MethaNet screens for a methane pathway inside a carbon estimate · BioPrior tests whether satellite bands see the biology claimed"
            accent="border-teal-500/25"
          />
          <div className="flex justify-center">
            <ArrowDown size={18} className="text-teal-500/40 rotate-180" />
          </div>
          <Layer
            index={2}
            tag="Layer 1 · Field Evidence"
            icon={Layers}
            title="The ground truth everything traces back to"
            items="Genomic samples · eDNA · flow cytometry · pigment data · monitoring stations → feeding MethaNet, BioPrior, AI Ecologist and Upper Colorado"
            accent="border-abyss-700"
          />
        </div>

        {/* Blue carbon stack callout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="p-8 md:p-10 rounded-lg border border-teal-500/20 bg-teal-900/10 backdrop-blur-sm"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-teal-400/60 mb-6">
            The blue-carbon stack as one due-diligence system
          </div>
          <div className="space-y-0">
            <StackRow
              name="HiGEBCA"
              role="estimates carbon stock"
              note="per mangrove polygon."
            />
            <StackRow
              name="MethaNet"
              role="checks the same site"
              note="for a quiet methane source that would erode the carbon credit's value."
            />
            <StackRow
              name="BioPrior"
              role="tests the remote-sensing layer"
              note="underneath all of it. Can it be trusted at the pixel level?"
              last
            />
          </div>
          <p className="text-sm text-sand-50/60 font-serif italic mt-6 pt-6 border-t border-teal-500/10">
            "Three layers of the same due-diligence question, built as one stack."
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default SystemsCompose;
